import { STAGES, StageData } from '../data/stages';

export interface StageRecord {
  /** Best stars ever earned for this stage (0..3). */
  stars: number;
  /** Cumulative points earned across all attempts. */
  cumulativeScore: number;
  cleared: boolean;
  attempts: number;
}

const STORAGE_KEY = 'yodajjom.stageProgress.v2';
const POINTS_KEY = 'yodajjom.points.v1';
const COOLDOWNS_KEY = 'yodajjom.cooldowns.v1';
const DEFAULT_POINTS = 300;

interface PersistedShape {
  records: Record<string, StageRecord>;
}

class StageProgressStore {
  private records: Record<string, StageRecord> = {};
  private points: number = DEFAULT_POINTS;
  /** stageId → epoch ms when the cooldown ends. Set when a player exhausts
   * all tosses; cleared when they refill (ad/points) or it expires naturally. */
  private cooldowns: Record<string, number> = {};
  private loaded = false;

  load() {
    if (this.loaded) return;
    this.loaded = true;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<PersistedShape>;
        this.records = parsed.records ?? {};
      } else {
        this.migrateFromV1();
      }
      const rawPoints = localStorage.getItem(POINTS_KEY);
      if (rawPoints) {
        const n = parseInt(rawPoints, 10);
        if (!Number.isNaN(n)) this.points = n;
      }
      const rawCd = localStorage.getItem(COOLDOWNS_KEY);
      if (rawCd) {
        const parsed = JSON.parse(rawCd) as Record<string, number>;
        // Drop already-expired entries on load to keep storage tidy.
        const now = Date.now();
        for (const [k, v] of Object.entries(parsed)) {
          if (typeof v === 'number' && v > now) this.cooldowns[k] = v;
        }
      }
    } catch (err) {
      console.warn('StageProgress load failed', err);
    }
  }

  private migrateFromV1() {
    try {
      const raw = localStorage.getItem('yodajjom.stageProgress.v1');
      if (!raw) return;
      const parsed = JSON.parse(raw) as { records?: Record<string, { stars?: number; bestScore?: number; cleared?: boolean; attempts?: number }> };
      const next: Record<string, StageRecord> = {};
      for (const [id, r] of Object.entries(parsed.records ?? {})) {
        next[id] = {
          stars: r.stars ?? 0,
          cumulativeScore: 0,
          cleared: r.cleared ?? false,
          attempts: r.attempts ?? 0,
        };
      }
      this.records = next;
      this.persist();
    } catch (err) {
      console.warn('StageProgress v1 migration failed', err);
    }
  }

  private persist() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ records: this.records }));
      localStorage.setItem(POINTS_KEY, String(this.points));
      localStorage.setItem(COOLDOWNS_KEY, JSON.stringify(this.cooldowns));
    } catch (err) {
      console.warn('StageProgress save failed', err);
    }
  }

  /** Start a fresh cooldown on `stageId` lasting `durationMs`. Persisted, so
   * closing the tab or reloading does not bypass it. */
  setCooldown(stageId: string, durationMs: number) {
    this.cooldowns[stageId] = Date.now() + durationMs;
    this.persist();
  }

  /** Cancel an active cooldown — used when the player pays (ad or points) to
   * refill immediately. */
  clearCooldown(stageId: string) {
    if (this.cooldowns[stageId] !== undefined) {
      delete this.cooldowns[stageId];
      this.persist();
    }
  }

  /** Returns ms remaining on the cooldown, or 0 if none / already expired.
   * Lazily evicts expired entries so callers can use this as the source of
   * truth without a separate "tick" job. */
  cooldownRemainingMs(stageId: string): number {
    const t = this.cooldowns[stageId];
    if (t === undefined) return 0;
    const remain = t - Date.now();
    if (remain <= 0) {
      delete this.cooldowns[stageId];
      this.persist();
      return 0;
    }
    return remain;
  }

  isCoolingDown(stageId: string): boolean {
    return this.cooldownRemainingMs(stageId) > 0;
  }

  getRecord(stageId: string): StageRecord {
    return (
      this.records[stageId] ?? {
        stars: 0,
        cumulativeScore: 0,
        cleared: false,
        attempts: 0,
      }
    );
  }

  recordAttempt(stageId: string, earnedScore: number, stars: number) {
    const prev = this.getRecord(stageId);
    const next: StageRecord = {
      stars: Math.max(prev.stars, stars),
      cumulativeScore: prev.cumulativeScore + earnedScore,
      cleared: prev.cleared || stars > 0,
      attempts: prev.attempts + 1,
    };
    this.records[stageId] = next;
    this.persist();
  }

  isUnlocked(stage: StageData): boolean {
    if (stage.index <= 1) return true;
    const prevIndex = stage.index - 1;
    const prev = STAGES.find((s) => s.index === prevIndex);
    if (!prev) return true;
    return this.getRecord(prev.id).cleared;
  }

  getPoints(): number {
    return this.points;
  }

  spendPoints(amount: number): boolean {
    if (this.points < amount) return false;
    this.points -= amount;
    this.persist();
    return true;
  }

  addPoints(amount: number) {
    this.points += amount;
    this.persist();
  }

  totalCumulativeScore(): number {
    return Object.values(this.records).reduce((sum, r) => sum + r.cumulativeScore, 0);
  }

  resetAll() {
    this.records = {};
    this.cooldowns = {};
    this.points = DEFAULT_POINTS;
    this.persist();
  }
}

export const stageProgress = new StageProgressStore();

/** Format a cooldown duration for compact display on UI badges.
 * - 1h 23m   (>= 1 hour)
 * - 23m 12s  (< 1 hour, >= 1 minute)
 * - 12s      (< 1 minute) */
export function formatCooldown(ms: number): string {
  const totalSec = Math.max(0, Math.ceil(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}
